// File: src/components/potus/POTUSTracker.jsx
import React, { useState } from 'react';
import {
    Container,
    Box,
    Tabs,
    Tab,
    CircularProgress,
    Typography,
    Paper,
    // Breadcrumbs,
    // Link
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import usePOTUSData from '../../hooks/usePOTUSData';
import { SECTOR_MAPPINGS } from '../../utils/constants';
import InfoBanner from './InfoBanner';
import SectorDropdown from './SectorDropdown';
import MarketPerformanceChart from './MarketPerformanceChart';
import PresidentialSchedule from './PresidentialSchedule';
import ExecutiveOrders from './ExecutiveOrders';
import TruthSocialPosts from './TruthSocialPosts';
// import HomeIcon from '@mui/icons-material/Home';

// Tab Panel component for accessibility
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`potus-tabpanel-${index}`}
            aria-labelledby={`potus-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

// Helper function for a11y
function a11yProps(index) {
    return {
        id: `potus-tab-${index}`,
        'aria-controls': `potus-tabpanel-${index}`,
    };
}

const POTUSTracker = () => {
    const theme = useTheme();
    const { data, marketData, loading, error } = usePOTUSData();
    const [tabValue, setTabValue] = useState(0);
    const [selectedSector, setSelectedSector] = useState('S&P500');

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSectorChange = (newSector) => {
        setSelectedSector(newSector);
    };

    // Check if market data is empty (still loading)
    const isMarketDataLoading = loading || Object.keys(marketData).length === 0;

    // Loading state for initial load (when everything is loading)
    if (loading && Object.keys(marketData).length === 0 && data.schedule.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="50vh"
                    flexDirection="column"
                    gap={2}
                >
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" color="text.secondary">
                        Loading POTUS Tracker...
                    </Typography>
                </Box>
            </Container>
        );
    }

    // Error state
    if (error) {
        return (
            <Container maxWidth="xl">
                <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="error" gutterBottom>
                        {error}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please try refreshing the page or contact support if the issue persists.
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Breadcrumbs 
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link
                    color="inherit"
                    href="/"
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Home
                </Link>
                <Typography color="text.primary">POTUS Tracker</Typography>
            </Breadcrumbs>
            */}

            {/* Header */}
            <Box
                sx={{
                    borderBottom: 2,
                    borderColor: theme.palette.primary.main,
                    mb: 3,
                    pb: 1
                }}
            >
                <Typography variant="h3" component="h1" fontWeight="bold">
                    POTUS Tracker
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Real-time tracking of presidential activities and market impact
                </Typography>
            </Box>

            {/* Info Banner - only show if market data is available */}
            {!isMarketDataLoading && (
                <InfoBanner
                    selectedSector={selectedSector}
                    marketData={marketData}
                />
            )}

            {/* Sector Dropdown */}
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <SectorDropdown
                    selectedSector={selectedSector}
                    onSectorChange={handleSectorChange}
                />
            </Box>

            {/* Market Performance Chart - with loading state */}
            <MarketPerformanceChart
                selectedSector={SECTOR_MAPPINGS[selectedSector]}
                marketData={marketData}
                loading={isMarketDataLoading}
            />

            {/* Main Navigation Tabs */}
            <Paper
                variant="stats"
                sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(19, 47, 76, 0.4)'
                        : 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(0, 0, 0, 0.1)',
                }}
            >
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="POTUS data tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            '& .MuiTab-root': {
                                fontWeight: 'bold',
                                fontSize: '1rem'
                            }
                        }}
                    >
                        <Tab label="Presidential Schedule" {...a11yProps(0)} />
                        <Tab label="Executive Orders" {...a11yProps(1)} />
                        <Tab label="Truth Social Posts" {...a11yProps(2)} />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <TabPanel value={tabValue} index={0}>
                    <PresidentialSchedule
                        data={data.schedule}
                        loading={loading}
                    />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <ExecutiveOrders
                        data={data.executiveOrders}
                        loading={loading}
                    />
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <TruthSocialPosts
                        data={data.truthPosts}
                        loading={loading}
                    />
                </TabPanel>
            </Paper>

            {/* Future: Related Tools Section */}
            {/*
            <Box sx={{ mt: 4 }}>
                <Paper
                    variant="stats"
                    sx={{
                        p: 3,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(19, 47, 76, 0.4)'
                            : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        border: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Related Tools
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Link
                            href="/newsletter"
                            sx={{
                                p: 2,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                textDecoration: 'none',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover
                                }
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                Market Newsletter
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Get a daily email with the top market news in bullet point format.
                            </Typography>
                        </Link>

                        <Link
                            href="/stock-screener"
                            sx={{
                                p: 2,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                textDecoration: 'none',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover
                                }
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                Stock Screener
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Build your Stock Screener to find profitable stocks.
                            </Typography>
                        </Link>

                        <Link
                            href="/watchlist/stocks"
                            sx={{
                                p: 2,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                textDecoration: 'none',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover
                                }
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                Watchlist
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Keep track of your favorite stocks in real-time.
                            </Typography>
                        </Link>
                    </Box>
                </Paper>
            </Box>
            */}
        </Container>
    );
};

export default POTUSTracker;